import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { getEstimateTypeLabel, PHOTO_REPORT_CAPTION_MAX_LENGTH } from "@rebuild/shared";
import type { PhotoReportCoverRowBody, PhotoReportExportOptionsBody } from "@rebuild/shared/models";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { EstimateFile } from "@/enums/estimate-file";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useGetEstimateResponsiveNotePhotosQuery } from "@/hooks/queries/responsive-note-photos";
import { useExportPhotoReportMutation } from "@/hooks/mutation/pdf-export";
import { appSelector } from "@/redux/selectors/app.selector";
import type { Estimation } from "@/interfaces/estimation.interface";
import {
  buildPhotoGalleryGroups,
  isPhotoGalleryItem,
  normalizeEstimateFilePhoto,
  normalizeResponsiveNotePhoto,
  sortEstimateFilesByRoomOrder,
} from "../../details-section/Photos/photos.utils";
import { useGetPhotoReportLetterheadQuery } from "@/hooks/queries/photo-report-letterhead";
import { formatPhotoLabelList, getPhotoLabels } from "../../details-section/Photos/photoLabels.utils";
import { PhotoReportPreview } from "./PhotoReportPreview";
import {
  buildPhotoReportExportRequest,
  getExportablePhotos,
  isPhotoIncluded,
  type PhotoSelectionState,
  type RoomTitleState,
} from "./photoReport.utils";

interface PhotoReportExportModalProps {
  open: boolean;
  estimateId: string;
  estimation: Estimation | undefined;
  onComplete: () => void;
  onCancel: () => void;
}

type PhotoReportOptionKey = "includeCoverPage" | "includeTableOfContents" | "includeCaptions" | "showPhotoNumberOverlay";

type PhotoReportOptions = Required<Pick<PhotoReportExportOptionsBody, PhotoReportOptionKey>>;

const DEFAULT_OPTIONS: PhotoReportOptions = {
  includeCoverPage: true,
  includeTableOfContents: false,
  includeCaptions: true,
  showPhotoNumberOverlay: true,
};

const OPTION_LABELS: { key: PhotoReportOptionKey; label: string }[] = [
  { key: "includeCoverPage", label: "Include cover page" },
  { key: "includeTableOfContents", label: "Include table of contents" },
  { key: "includeCaptions", label: "Include captions" },
  { key: "showPhotoNumberOverlay", label: "Show photo numbers" },
];

// Seed the editable cover fields from the estimate. The user can override any value in the preview;
// what they leave here is what the PDF renders (WYSIWYG).
function buildCoverRows(estimation: Estimation | undefined): PhotoReportCoverRowBody[] {
  return [
    { label: "Project", value: estimation?.projectName ?? "" },
    { label: "Claim ID", value: estimation?.estimateReferenceNumber ?? "" },
    { label: "Type of Loss", value: getEstimateTypeLabel(estimation?.estimateType) },
    { label: "Address", value: estimation?.propertyAddress ?? "" },
    { label: "Date of Loss", value: estimation?.dateOfLoss ?? "" },
  ];
}

export default function PhotoReportExportModal({
  open,
  estimateId,
  estimation,
  onComplete,
  onCancel,
}: PhotoReportExportModalProps) {
  const organizationName = useSelector(appSelector.organizationName) ?? "";
  const { rooms } = useEstimateDetail(estimateId);
  const {
    data: responsiveNotePhotos = [],
    isLoading: isResponsiveLoading,
  } = useGetEstimateResponsiveNotePhotosQuery(open ? estimateId : undefined);
  const { mutateAsync: exportPhotoReport, isPending } = useExportPhotoReportMutation();
  const { data: letterhead } = useGetPhotoReportLetterheadQuery(open ? estimateId : undefined);

  const [selection, setSelection] = useState<PhotoSelectionState>(new Map());
  const [options, setOptions] = useState<PhotoReportOptions>(DEFAULT_OPTIONS);
  const [roomTitles, setRoomTitles] = useState<RoomTitleState>(new Map());

  const defaultCoverRows = useMemo(() => buildCoverRows(estimation), [estimation]);
  const [coverRows, setCoverRows] = useState<PhotoReportCoverRowBody[]>(defaultCoverRows);

  const files = useMemo(() => estimation?.files ?? [], [estimation?.files]);

  const groups = useMemo(() => {
    const imageFiles = files.filter((file) => file.fileType === EstimateFile.IMAGE && !file.deleted);
    const filesSorted = sortEstimateFilesByRoomOrder(imageFiles, rooms);
    const estimatePhotos = filesSorted.map(normalizeEstimateFilePhoto).filter(isPhotoGalleryItem);
    const responsiveNotePhotoItems = responsiveNotePhotos.map(normalizeResponsiveNotePhoto);
    return buildPhotoGalleryGroups({ estimatePhotos, responsiveNotePhotos: responsiveNotePhotoItems, rooms });
  }, [files, rooms, responsiveNotePhotos]);

  const exportablePhotos = useMemo(() => getExportablePhotos(groups), [groups]);
  const totalCount = exportablePhotos.length;

  // Read through a ref so seeding never becomes a dependency: the photo query polls for thumbnails,
  // and depending on it directly would re-run initialize mid-session and wipe the user's edits.
  const exportablePhotosRef = useRef(exportablePhotos);
  exportablePhotosRef.current = exportablePhotos;
  const hasInitializedRef = useRef(false);

  // Reset to a clean slate (all photos included, all options on, cover fields re-seeded from the
  // estimate) whenever the dialog opens. Captions are seeded from each photo's labels so the report
  // inherits the labelling work already done in the gallery; they stay editable and export-only.
  const initialize = useCallback(() => {
    const seeded: PhotoSelectionState = new Map();
    for (const photo of exportablePhotosRef.current) {
      const labels = getPhotoLabels(photo);
      if (labels.length > 0) {
        seeded.set(photo.galleryId, {
          included: true,
          caption: formatPhotoLabelList(labels).slice(0, PHOTO_REPORT_CAPTION_MAX_LENGTH),
        });
      }
    }
    setSelection(seeded);
    setOptions(DEFAULT_OPTIONS);
    setCoverRows(defaultCoverRows);
    setRoomTitles(new Map());
  }, [defaultCoverRows]);

  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    initialize();
  }, [open, initialize]);

  // Live global-sequential numbering over the *included* photos, in display (= PDF) order.
  const photoNumberById = useMemo(() => {
    const map = new Map<string, number>();
    let counter = 0;
    for (const photo of exportablePhotos) {
      if (isPhotoIncluded(selection, photo.galleryId)) {
        counter += 1;
        map.set(photo.galleryId, counter);
      }
    }
    return map;
  }, [exportablePhotos, selection]);

  const includedCount = photoNumberById.size;

  const togglePhoto = useCallback((galleryId: string) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const entry = next.get(galleryId) ?? { included: true, caption: "" };
      next.set(galleryId, { ...entry, included: !(entry.included !== false) });
      return next;
    });
  }, []);

  const changeCaption = useCallback((galleryId: string, caption: string) => {
    setSelection((prev) => {
      const next = new Map(prev);
      const entry = next.get(galleryId) ?? { included: true, caption: "" };
      next.set(galleryId, { ...entry, caption });
      return next;
    });
  }, []);

  const setIncluded = useCallback((galleryIds: string[], include: boolean) => {
    setSelection((prev) => {
      const next = new Map(prev);
      for (const galleryId of galleryIds) {
        const entry = next.get(galleryId) ?? { included: true, caption: "" };
        next.set(galleryId, { ...entry, included: include });
      }
      return next;
    });
  }, []);

  const allGalleryIds = useMemo(() => exportablePhotos.map((photo) => photo.galleryId), [exportablePhotos]);

  const toggleOption = useCallback((key: PhotoReportOptionKey) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const changeCoverRow = useCallback((index: number, value: string) => {
    setCoverRows((prev) => prev.map((row, i) => (i === index ? { ...row, value } : row)));
  }, []);

  const changeRoomTitle = useCallback((groupId: string, title: string) => {
    setRoomTitles((prev) => new Map(prev).set(groupId, title));
  }, []);

  const handleExport = useCallback(async () => {
    if (includedCount === 0) {
      return;
    }
    const body = buildPhotoReportExportRequest(groups, selection, options, coverRows, roomTitles);
    try {
      const bytes = await exportPhotoReport({ estimateId, body });
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const filename = estimation?.projectName ? `${estimation.projectName} - Photo Report.pdf` : `photo-report-${estimateId}.pdf`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Photo report exported");
      onComplete();
    } catch {
      toast.error("Failed to export photo report");
    }
  }, [includedCount, groups, selection, options, coverRows, roomTitles, exportPhotoReport, estimateId, estimation?.projectName, onComplete]);

  const isEmpty = !isResponsiveLoading && totalCount === 0;

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="flex max-h-[90%] w-full max-w-7xl flex-col overflow-hidden rounded-lg bg-white! p-6 shadow-lg">
        <div className="flex w-full flex-1 flex-row gap-6 overflow-hidden">
          {isResponsiveLoading && totalCount === 0 ? (
            <div className="flex flex-[2] items-center justify-center rounded-lg bg-gray-100">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-3 text-gray-600">Loading photos…</span>
            </div>
          ) : (
            <PhotoReportPreview
              organizationName={organizationName}
              letterheadUrl={letterhead?.url ?? null}
              letterheadAlignment={letterhead?.previewAlignment}
              includeCoverPage={options.includeCoverPage}
              includeTableOfContents={options.includeTableOfContents}
              coverRows={coverRows}
              onCoverRowChange={changeCoverRow}
              groups={groups}
              roomTitles={roomTitles}
              onRoomTitleChange={changeRoomTitle}
              selection={selection}
              photoNumberById={photoNumberById}
              showPhotoNumberOverlay={options.showPhotoNumberOverlay}
              includeCaptions={options.includeCaptions}
              onTogglePhoto={togglePhoto}
              onCaptionChange={changeCaption}
              onToggleRoom={setIncluded}
            />
          )}

          {/* Right side - Controls */}
          <div className="flex flex-1 flex-col overflow-y-auto">
            <div className="mb-8">
              <h2 className="mb-2 text-2xl leading-7 font-semibold">Photo Report Export</h2>
              <p className="text-sm text-gray-600">Choose photos and captions, then export a PDF report.</p>
            </div>

            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold">Options</h3>
              <div className="space-y-3">
                {OPTION_LABELS.map(({ key, label }) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox checked={options[key]} onCheckedChange={() => toggleOption(key)} />
                    <label className="cursor-pointer text-sm text-gray-700" onClick={() => toggleOption(key)}>
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold">Photos</h3>
              {isEmpty ? (
                <p className="text-sm text-gray-500">This estimate has no photos to export.</p>
              ) : (
                <>
                  <p className="mb-3 text-sm text-gray-600">
                    {includedCount} of {totalCount} included
                  </p>
                  <div className="flex gap-4 text-xs font-medium">
                    <button
                      type="button"
                      className="text-indigo-600 hover:text-indigo-700"
                      onClick={() => setIncluded(allGalleryIds, true)}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-indigo-600 hover:text-indigo-700"
                      onClick={() => setIncluded(allGalleryIds, false)}
                    >
                      Clear all
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="flex-1" />

            <div className="border-accent-foreground flex flex-col gap-3 border-t pt-4">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="outline" onClick={initialize}>
                Reset to original
              </Button>
              <Button variant="default" onClick={handleExport} disabled={includedCount === 0 || isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting PDF...
                  </>
                ) : (
                  "Save & Export PDF"
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
